/**
 * Sign-in behaviour, with no DOM and no knowledge of Google's library.
 *
 * Both collaborators are interfaces, so every path here — including the ones
 * that only happen when the network or Google is broken — is reachable from a
 * test with plain objects.
 */

import { ApiError } from "./api-client.js";
import type { SessionResponse, SessionUser } from "./contract.js";
import type { GoogleSignIn } from "./google-sign-in.js";
import type { AuthView } from "./auth-view.js";

export const SIGN_IN_UNAVAILABLE =
  "Sign-in is temporarily unavailable. Please reload the page.";
export const SESSION_ENDED = "Your session ended. Please sign in again.";
export const UNREACHABLE = "Could not reach the server. Please try again.";

/** The slice of the API client this controller needs. */
export interface SessionApi {
  getSession(): Promise<SessionResponse>;
  signIn(idToken: string): Promise<SessionResponse>;
  signOut(): Promise<void>;
}

export type AuthControllerOptions = {
  readonly view: AuthView;
  readonly api: SessionApi;
  readonly googleSignIn: GoogleSignIn;
  /**
   * Called whenever the page becomes signed in — on load with an existing
   * session, and again after a fresh sign-in. The admin page uses it to know
   * when it may fetch; the main page has nothing to do and omits it.
   */
  readonly onSignedIn?: (user: SessionUser) => void;
};

export class AuthController {
  private inFlight = false;

  constructor(private readonly options: AuthControllerOptions) {}

  async start(): Promise<void> {
    const { view } = this.options;

    view.render({ kind: "checking" });
    view.onSignOut(() => {
      void this.signOut();
    });

    let user: SessionUser | null = null;

    try {
      user = (await this.options.api.getSession()).user;
    } catch {
      // An unreachable server is indistinguishable from being signed out, and
      // the signed-out screen is the honest thing to show either way.
    }

    if (user !== null) {
      this.showSignedIn(user);
      return;
    }

    await this.showSignedOut();
  }

  /** Called when any request comes back 401: the cookie expired mid-session. */
  handleUnauthenticated(): void {
    void this.showSignedOut(SESSION_ENDED);
  }

  async signOut(): Promise<void> {
    this.options.googleSignIn.forgetUser();

    try {
      await this.options.api.signOut();
    } catch {
      // The server may be unreachable, but the intent was to end the session,
      // so the UI still returns to signed out rather than pretending otherwise.
    }

    await this.showSignedOut();
  }

  private async signIn(idToken: string): Promise<void> {
    if (this.inFlight) return;

    this.inFlight = true;

    try {
      const session = await this.options.api.signIn(idToken);

      if (session.user === null) {
        await this.showSignedOut(SIGN_IN_UNAVAILABLE);
        return;
      }

      this.showSignedIn(session.user);
    } catch (error) {
      await this.showSignedOut(
        error instanceof ApiError ? error.message : UNREACHABLE,
      );
    } finally {
      this.inFlight = false;
    }
  }

  private showSignedIn(user: SessionUser): void {
    this.options.view.render({ kind: "signedIn", user });
    this.options.onSignedIn?.(user);
  }

  private async showSignedOut(message?: string): Promise<void> {
    const { view, googleSignIn } = this.options;

    view.render(
      message === undefined ? { kind: "signedOut" } : { kind: "signedOut", message },
    );

    try {
      await googleSignIn.mount(view.signInContainer(), (idToken) => {
        void this.signIn(idToken);
      });
    } catch {
      // The script is blocked, offline, or stripped. Say so plainly rather than
      // leaving a panel with no button in it.
      view.render({ kind: "signedOut", message: SIGN_IN_UNAVAILABLE });
    }
  }
}
